import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import LoadingScreen from '@/components/loading-screen';
import { me } from '@/services/auth';
import { CACHE_TTL } from '@/services/constants';

// Meu perfil dentro de conta compartilhada (CBK-676, critério 5.10 da CBK-595): a página mostra e
// altera o cadastro de quem está logado, não o do titular. As chamadas dela saem sem o header da
// impersonação, e o cache fica separado do da conta em uso para os dois não se misturarem.
// Fora do provider, `scoped` devolve o próprio serviço: as outras telas chamam como sempre.

const LOGGED_USER_REQUEST = { skipImpersonation: true };

export const LOGGED_USER_QUERY_SCOPE = 'logged-user';

export const LOGGED_USER_QUERY_KEY = ['user', LOGGED_USER_QUERY_SCOPE];

const OUTSIDE_SCOPE = { asLoggedUser: false, loggedUser: null };

const LoggedUserScopeContext = createContext(OUTSIDE_SCOPE);

export function LoggedUserScopeProvider({ children }) {
  // O `user` dos contextos de sessão é o da conta em uso, que aqui é a do titular.
  const { data: loggedUser, isLoading } = useQuery({
    queryKey: LOGGED_USER_QUERY_KEY,
    queryFn: () => me({}, LOGGED_USER_REQUEST),
    staleTime: CACHE_TTL,
  });

  const value = useMemo(
    () => ({ asLoggedUser: true, loggedUser: loggedUser ?? null }),
    [loggedUser]
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <LoggedUserScopeContext.Provider value={value}>{children}</LoggedUserScopeContext.Provider>
  );
}

LoggedUserScopeProvider.propTypes = {
  children: PropTypes.node,
};

export function useLoggedUserScope() {
  const { asLoggedUser, loggedUser } = useContext(LoggedUserScopeContext);

  return useMemo(
    () => ({
      asLoggedUser,
      loggedUser,
      scoped: (service) =>
        asLoggedUser ? (...args) => service(...args, LOGGED_USER_REQUEST) : service,
      scopedKey: (queryKey) => (asLoggedUser ? [...queryKey, LOGGED_USER_QUERY_SCOPE] : queryKey),
    }),
    [asLoggedUser, loggedUser]
  );
}
